import endpoints from '@/config/endpoints'
import { CookieName } from '@/config/cookies'
import Cookies from 'js-cookie'

export type HeaderContactInfo = {
    phone: string
    openingHours: string
    customerServiceLabel: string
}

const PHONE_PATTERN = /(?:\+|00)?\d[\d\s().-]{6,}\d/
const OPENING_HOURS_KEYWORDS = [
    'lundi',
    'vendredi',
    'samedi',
    'dimanche',
    'monday',
    'friday',
    'saturday',
    'sunday',
    'maandag',
    'vrijdag',
    'zaterdag',
    'zondag',
]

const cleanText = (value: string | null | undefined): string =>
    (value || '').replace(/\s+/g, ' ').trim()

const getPhoneFromElement = (root: ParentNode): string => {
    const desktopPhoneText = cleanText(root.querySelector('.header-desktop-customer-info .caption')?.textContent)
    if (desktopPhoneText) return desktopPhoneText

    const popupTitlePhone = cleanText(root.querySelector('.header-popup-contact-wrapper .title-4')?.textContent).match(PHONE_PATTERN)
    if (popupTitlePhone) return popupTitlePhone[0].trim()

    const telLink = root.querySelector('a[href^="tel:"]')
    const telHrefPhone = cleanText(telLink?.getAttribute('href')).replace(/^tel:/, '').match(PHONE_PATTERN)
    if (telHrefPhone) return telHrefPhone[0].trim()

    const telTextPhone = cleanText(telLink?.textContent).match(PHONE_PATTERN)
    if (telTextPhone) return telTextPhone[0].trim()

    const phoneMatch = cleanText(root.textContent).match(PHONE_PATTERN)
    return phoneMatch ? phoneMatch[0].trim() : ''
}

const getOpeningHoursFromElement = (root: ParentNode): string => {
    const headerBubbleText = cleanText(root.querySelector('.header-bubble-text')?.textContent)
    if (headerBubbleText) return headerBubbleText

    const text = cleanText(root.textContent)
    if (!text) return ''

    const sentences = text
        .split(/(?<=[.!?])\s+/)
        .map(cleanText)
        .filter(Boolean)

    const firstOpeningSentenceIndex = sentences.findIndex(sentence => {
        const normalizedSentence = sentence.toLowerCase()
        return OPENING_HOURS_KEYWORDS.some(keyword => normalizedSentence.includes(keyword))
    })

    if (firstOpeningSentenceIndex === -1) return ''

    const openingSentences = [sentences[firstOpeningSentenceIndex]]
    const nextSentence = sentences[firstOpeningSentenceIndex + 1]
    if (nextSentence) {
        const normalizedNextSentence = nextSentence.toLowerCase()
        const mentionsClosedDays = OPENING_HOURS_KEYWORDS.some(keyword => normalizedNextSentence.includes(keyword))
        if (mentionsClosedDays) openingSentences.push(nextSentence)
    }

    return openingSentences.join(' ')
}

const getCustomerServiceLabelFromElement = (root: ParentNode): string => {
    const desktopLabel = cleanText(root.querySelector('.header-desktop-customer-info .subcaption')?.textContent)
    if (desktopLabel) return desktopLabel

    const popupTitle = cleanText(root.querySelector('.header-popup-contact-wrapper .title-4')?.textContent)
    return popupTitle.replace(PHONE_PATTERN, '').replace(':', '').trim()
}

const extractHeaderContactInfo = (root: ParentNode): HeaderContactInfo => ({
    phone: getPhoneFromElement(root),
    openingHours: getOpeningHoursFromElement(root),
    customerServiceLabel: getCustomerServiceLabelFromElement(root),
})

const getHeaderContactUrl = (): string => {
    let menuDomain = window.env.VUE_APP_MENU_DOMAIN || window.location.hostname
    const company = Cookies.get(CookieName.COMPANY)
    if (window.env.VUE_APP_LOCALE === 'fr-FR-CSE' && company) {
        menuDomain += `/${company}`
    }
    return `${endpoints.menu}/${menuDomain}/header.html`
}

export const getHeaderContactInfo = async (): Promise<HeaderContactInfo> => {
    const injectedHeader = document.querySelector('#header-wrapper header') || document.querySelector('#header-wrapper')
    if (injectedHeader) {
        const contactInfo = extractHeaderContactInfo(injectedHeader)
        if (contactInfo.phone && contactInfo.openingHours) return contactInfo
    }

    const response = await fetch(getHeaderContactUrl())
    const text = await response.text()
    const doc = new DOMParser().parseFromString(text, 'text/html')
    return extractHeaderContactInfo(doc)
}
